import { useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ConfirmModal } from '@/components/feedback/ConfirmModal';
import { SlidePanel } from '@/components/feedback/SlidePanel';
import { toast } from '@/components/feedback/useToast';

import { useInviteMedicalStaff } from '../hooks/use-medical-staff-mutations';
import { PHONE_REGEX, normalizePhone, toPhoneInputValue } from '@/shared/utils/phone';

import { useMedicalStaffOptionsQuery } from '../hooks/use-medical-staff';
import { useTranslateOption } from '../hooks/use-translate-option';
import { MedicalStaffApiError } from '../types/medical-staff.types';
import { MedicalStaffInvitePreviewModal } from './MedicalStaffInvitePreviewModal';

// Email format: local@domain.TLD where the TLD is >=2 ASCII letters (aligns the FE to the spec email rule).
const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

// Send-time chrome string for the preview card meta (YYYY-MM-DD HH:mm), captured once
// when the preview opens so it doesn't tick while the modal is up.
function formatSentAt(now: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

const INITIAL_FORM = {
  name: '',
  email: '',
  phone: '',
  department: '',
  jobRole: '',
};

type FieldKey = 'name' | 'email' | 'phone' | 'department' | 'jobRole';

// PRD 03 §3.3 시나리오 2·3 — 초대 폼의 필수 필드. 버튼 활성 판정과 제출 시 전체 검증이
// 같은 목록을 봐야 한 쪽만 바뀌는 일이 없다.
const REQUIRED_FIELDS: FieldKey[] = ['name', 'email', 'phone', 'department', 'jobRole'];

interface MedicalStaffInvitePanelProps {
  open: boolean;
  onClose: () => void;
}

// PRD 03 §3.3 (v1.2 / Q13): department and job-role options are DB-preloaded
// and globally shared. The "+ Add" affordance was removed — admins pick from
// the canonical option pool only. The add-option mutations remain in
// use-medical-staff-mutations but no FE caller invokes them after this slice.
export function MedicalStaffInvitePanel({ open, onClose }: MedicalStaffInvitePanelProps) {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  const { translateDepartment, translateJobRole } = useTranslateOption();
  const { data: options } = useMedicalStaffOptionsQuery();
  const inviteMutation = useInviteMedicalStaff();

  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const [isDirty, setIsDirty] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSentAt, setPreviewSentAt] = useState('');

  const departments = options?.departments ?? [];
  const jobRoles = options?.jobRoles ?? [];

  const reset = () => {
    setForm(INITIAL_FORM);
    setErrors({});
    setIsDirty(false);
    setShowDiscard(false);
    setPreviewOpen(false);
  };

  const update = <K extends FieldKey>(key: K, value: (typeof form)[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setIsDirty(true);
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  // Per-field validation — shared by the on-blur handler (immediate feedback when
  // the user leaves a field) and the submit-time full sweep.
  const validateField = (key: FieldKey, f: typeof form): string | undefined => {
    switch (key) {
      case 'name': {
        const name = f.name.trim();
        if (name.length < 2 || name.length > 50) {
          return t('medicalStaffManagement.fields.name.errorRequired');
        }
        return undefined;
      }
      case 'email': {
        const email = f.email.trim();
        if (!email) return t('medicalStaffManagement.fields.email.errorRequired');
        if (!EMAIL_REGEX.test(email)) {
          return t('medicalStaffManagement.fields.email.errorInvalid');
        }
        return undefined;
      }
      case 'phone': {
        const normalizedPhone = normalizePhone(f.phone);
        if (!normalizedPhone) return t('medicalStaffManagement.fields.phone.errorRequired');
        if (!PHONE_REGEX.test(normalizedPhone)) {
          return t('medicalStaffManagement.fields.phone.errorInvalid');
        }
        return undefined;
      }
      case 'department':
        return f.department
          ? undefined
          : t('medicalStaffManagement.fields.department.errorRequired');
      case 'jobRole':
        return f.jobRole
          ? undefined
          : t('medicalStaffManagement.fields.jobRole.errorRequired');
    }
  };

  // Validate a single field when the user leaves it (blur). Clears the error when
  // the field becomes valid so the message doesn't linger after a fix.
  const handleBlur = (key: FieldKey) => {
    setErrors((prev) => {
      const err = validateField(key, form);
      const next = { ...prev };
      if (err) next[key] = err;
      else delete next[key];
      return next;
    });
  };

  // SHCT-597 (CR-01 §2) — [초대 발송] 버튼은 필수 필드가 전부 통과할 때까지 비활성이다.
  // 근거는 두 곳이고, 판정 기준은 더 상위 문서를 따른다:
  //   · [공통] 00 §1.2 전역 원칙 — "액션 버튼: 필수 필드 전체 **통과** 전까지 비활성, 재변경 시 재비활성".
  //     "통과"라 형식 오류(전화번호 `^010\d{8}$`·이메일 형식)도 비활성 사유에 포함된다.
  //   · PRD 03 §3.3 — "전화번호·담당 과·직무 중 하나라도 미입력·미선택 시 [초대 발송] 버튼은 비활성화",
  //     조건분기 "IF 필수 필드 미입력 → THEN 해당 필드 하단에 에러 메시지 표시, 초대 발송 버튼 비활성화".
  // PRD는 "미입력"(빈 값)까지만 적었지만 [공통] 00 §1.1이 "본 문서와 개별 PRD가 충돌하면 본 문서가
  // 우선한다"고 못박으므로 더 넓은 §1.2 기준을 쓴다. 빈 값도 통과 실패라 PRD 조건도 함께 만족한다.
  // 필수 필드는 §3.3 시나리오 2의 이름·이메일·전화번호·담당 과·직무 5개.
  // 클릭 시점의 전체 검증(handleSubmit → validate)은 그대로 남는다 — 버튼이 열려 있는 상태에서만
  // 도달하지만, 서버 왕복 전 마지막 방어선이자 §3.3 시나리오 4가 명시한 동선이다.
  const hasBlockingFieldError = REQUIRED_FIELDS.some((key) => validateField(key, form) !== undefined);

  const validate = (): boolean => {
    const next: Partial<Record<FieldKey, string>> = {};
    REQUIRED_FIELDS.forEach((key) => {
      const err = validateField(key, form);
      if (err) next[key] = err;
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const closeAndReset = () => {
    onClose();
    // Defer reset until slide-out completes (~300 ms) so the form doesn't
    // visibly flash to its empty state during the animation.
    window.setTimeout(reset, 300);
  };

  const handleClose = () => {
    if (inviteMutation.isPending) return;
    if (isDirty) {
      setShowDiscard(true);
      return;
    }
    closeAndReset();
  };

  const handleDiscardConfirm = () => {
    setShowDiscard(false);
    closeAndReset();
  };

  const showInviteSuccessToast = (invitedEmail: string) => {
    toast.success(t('medicalStaffManagement.toasts.inviteSuccess'), {
      action: {
        label: t('medicalStaffManagement.toasts.invitePreviewAction'),
        onClick: () =>
          navigate(
            `/medical-staff-management/invite-email-preview?email=${encodeURIComponent(invitedEmail)}`,
          ),
      },
    });
  };

  // The actual invite send — shared by the form's [초대 발송] button and the
  // pre-send preview modal's [✉ 초대 발송] button. On a field-level API error it
  // closes the preview so the inline errors are visible back on the form.
  const performInvite = async () => {
    if (inviteMutation.isPending) return;
    const invitedEmail = form.email.trim();
    try {
      await inviteMutation.mutateAsync({
        name: form.name.trim(),
        email: invitedEmail,
        phone: normalizePhone(form.phone),
        department: form.department,
        jobRole: form.jobRole,
      });
      closeAndReset();
      showInviteSuccessToast(invitedEmail);
    } catch (e) {
      if (e instanceof MedicalStaffApiError && e.code === 'PHONE_DUPLICATE') {
        // Anti-enumeration: a phone already used by another staff shows the single shared generic
        // message (locale-aware), inline on the phone field — never the server's Korean text.
        setPreviewOpen(false);
        setErrors((prev) => ({ ...prev, phone: t('errors.phoneRejected', { ns: 'common' }) }));
        return;
      }
      if (e instanceof MedicalStaffApiError && e.fields) {
        setPreviewOpen(false);
        setErrors(e.fields as Partial<Record<FieldKey, string>>);
        return;
      }
      // System/unknown API failures show the action-specific message, not the raw
      // mapped `e.message` (which is the generic UNKNOWN fallback). Field & business
      // errors are handled above (fields / PHONE_DUPLICATE).
      toast.error(t('medicalStaffManagement.toasts.inviteError'));
    }
  };

  const handleSubmit = async () => {
    if (inviteMutation.isPending) return;
    if (!validate()) return;
    await performInvite();
  };

  // [📧 이메일 미리보기] — validate first (prototype parity), then show the
  // confirmation modal populated with the live form values.
  const handlePreview = () => {
    if (inviteMutation.isPending) return;
    if (!validate()) return;
    setPreviewSentAt(formatSentAt(new Date()));
    setPreviewOpen(true);
  };

  return (
    <>
      <SlidePanel
        open={open}
        title={t('medicalStaffManagement.invitePanel.title')}
        width={480}
        onClose={handleClose}
      >
        <div className="form-field">
          <label className="form-field__label">
            {t('medicalStaffManagement.fields.name.label')}{' '}
            <span className="required">*</span>
          </label>
          <input
            type="text"
            className={`form-field__input${errors.name ? ' form-field__input--error' : ''}`}
            placeholder={t('medicalStaffManagement.fields.name.placeholder')}
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
            onBlur={() => handleBlur('name')}
          />
          {errors.name && <p className="form-field__error">{errors.name}</p>}
        </div>

        <div className="form-field">
          <label className="form-field__label">
            {t('medicalStaffManagement.fields.email.label')}{' '}
            <span className="required">*</span>
          </label>
          <input
            type="email"
            className={`form-field__input${errors.email ? ' form-field__input--error' : ''}`}
            placeholder={t('medicalStaffManagement.fields.email.placeholder')}
            value={form.email}
            onChange={(e) => update('email', e.target.value)}
            onBlur={() => handleBlur('email')}
          />
          {errors.email && <p className="form-field__error">{errors.email}</p>}
        </div>

        <div className="form-field">
          <label className="form-field__label">
            {t('medicalStaffManagement.fields.phone.label')}{' '}
            <span className="required">*</span>
          </label>
          <input
            type="tel"
            className={`form-field__input${errors.phone ? ' form-field__input--error' : ''}`}
            placeholder={t('medicalStaffManagement.fields.phone.placeholder')}
            inputMode="numeric"
            value={form.phone}
            onChange={(e) => update('phone', toPhoneInputValue(e.target.value))}
            onBlur={() => handleBlur('phone')}
          />
          {errors.phone && <p className="form-field__error">{errors.phone}</p>}
        </div>

        <div className="form-field">
          <label className="form-field__label">
            {t('medicalStaffManagement.fields.department.label')}{' '}
            <span className="required">*</span>
          </label>
          <select
            className={`form-field__select${errors.department ? ' form-field__select--error' : ''}`}
            value={form.department}
            onChange={(e) => update('department', e.target.value)}
            onBlur={() => handleBlur('department')}
          >
            <option value="">
              {t('medicalStaffManagement.fields.department.placeholder')}
            </option>
            {departments.map((d) => (
              <option key={d} value={d}>
                {translateDepartment(d)}
              </option>
            ))}
          </select>
          {errors.department && (
            <p className="form-field__error">{errors.department}</p>
          )}
        </div>

        <div className="form-field">
          <label className="form-field__label">
            {t('medicalStaffManagement.fields.jobRole.label')}{' '}
            <span className="required">*</span>
          </label>
          <select
            className={`form-field__select${errors.jobRole ? ' form-field__select--error' : ''}`}
            value={form.jobRole}
            onChange={(e) => update('jobRole', e.target.value)}
            onBlur={() => handleBlur('jobRole')}
          >
            <option value="">
              {t('medicalStaffManagement.fields.jobRole.placeholder')}
            </option>
            {jobRoles.map((r) => (
              <option key={r} value={r}>
                {translateJobRole(r)}
              </option>
            ))}
          </select>
          {errors.jobRole && <p className="form-field__error">{errors.jobRole}</p>}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 'var(--space-2)',
            marginTop: 'var(--space-6)',
          }}
        >
          <button
            type="button"
            className="btn btn-default btn-secondary"
            onClick={handlePreview}
            disabled={inviteMutation.isPending}
            title={t('medicalStaffManagement.invitePreviewModal.previewButton')}
          >
            {t('medicalStaffManagement.invitePreviewModal.previewButton')}
          </button>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <button
              type="button"
              className="btn btn-default btn-secondary"
              onClick={handleClose}
              disabled={inviteMutation.isPending}
            >
              {t('medicalStaffManagement.invitePanel.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-default btn-primary"
              onClick={handleSubmit}
              disabled={inviteMutation.isPending || hasBlockingFieldError}
              title={
                hasBlockingFieldError
                  ? t('medicalStaffManagement.tooltips.completeRequiredFields')
                  : undefined
              }
            >
              {inviteMutation.isPending
                ? t('medicalStaffManagement.invitePanel.submitting')
                : t('medicalStaffManagement.invitePanel.submit')}
            </button>
          </div>
        </div>
      </SlidePanel>

      <MedicalStaffInvitePreviewModal
        open={previewOpen}
        name={form.name.trim()}
        email={form.email.trim()}
        departmentLabel={translateDepartment(form.department)}
        jobRoleLabel={translateJobRole(form.jobRole)}
        sentAt={previewSentAt}
        submitting={inviteMutation.isPending}
        onEdit={() => setPreviewOpen(false)}
        onConfirm={performInvite}
      />

      <ConfirmModal
        open={showDiscard}
        title={t('medicalStaffManagement.invitePanel.discardTitle')}
        message={t('medicalStaffManagement.invitePanel.discardMessage')}
        confirmLabel={t('medicalStaffManagement.modals.discard.confirm')}
        cancelLabel={t('medicalStaffManagement.modals.discard.cancel')}
        confirmVariant="destructive"
        onConfirm={handleDiscardConfirm}
        onCancel={() => setShowDiscard(false)}
      />
    </>
  );
}
